import { useEffect } from "react";
import { Control, useForm, UseFormReturn } from "react-hook-form";
import { useRecoilValue } from "recoil";

import {
  hasKeys,
  IExtendableDto,
  Log,
  METADATA_KEY,
  RydeUiUnit,
  shouldResetOnAddNew,
  shouldResetOnEditOrDuplicate,
  yupValidationSchemaSF,
} from "@ryde/shared";

import { useYupValidationResolver } from "../use-yup-validation";
import { useConditionalFormRevalidate } from "../validation";
import {
  getDtoForDuplicateReset,
  getDuplicateFieldEntry,
  getDuplicateInitialPatch,
} from "./duplicate-entity.helpers";

interface IUseEditFormProviderArguments {
  entity: RydeUiUnit;
  dto?: IExtendableDto;
  isEdit: boolean;
  isDuplicate: boolean;
}

export const useEditFormProvider = ({
  entity,
  dto,
  isEdit,
  isDuplicate,
}: IUseEditFormProviderArguments): {
  methods: UseFormReturn<IExtendableDto>;
  control: Control<IExtendableDto>;
} => {
  const yupSchemaDynamic = useRecoilValue(yupValidationSchemaSF(entity));
  const yupDynamicResolver = useYupValidationResolver(yupSchemaDynamic, entity);
  const methods = useForm<IExtendableDto>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    resolver: yupDynamicResolver,
  });

  const {
    reset,
    clearErrors,
    control,
    setValue,
    getValues,
    trigger,
    formState: { isDirty },
  } = methods;

  useEffect(() => {
    if (!isDirty) {
      /*
       * Moved under this condition `clearErrors()` according to the issue yup localized errors, in some reason,
       * cached by entity
       *
       * RYDE-3396
       * */
      clearErrors();

      if (dto) {
        if (shouldResetOnEditOrDuplicate({ dto, isEdit, isDuplicate })) {
          Log.debug(`form reset called for entity ${entity}, dto:`, dto);
          clearErrors();
          const dtoToReset = getDtoForDuplicateReset(
            entity,
            dto as Record<string, unknown>,
            isDuplicate,
          );
          reset(dtoToReset);
        } else if (shouldResetOnAddNew({ dto, isEdit, isDuplicate })) {
          Log.debug(
            `Setting initial values called for entity ${entity}, dto:`,
            dto,
          );
          clearErrors();
          const initialPatch = getDuplicateInitialPatch(entity, isDuplicate);
          Object.entries(initialPatch).forEach(([field, value]) => {
            setValue(field, value, { shouldDirty: true });
          });
          Object.entries(dto).forEach(([field, value]) => {
            const entry = getDuplicateFieldEntry(
              entity,
              field,
              value,
              isDuplicate,
            );
            if (entry.skip) {
              return;
            }

            if (field === METADATA_KEY && hasKeys(value)) {
              Object.entries(value).forEach(([metaField, metaValue]) => {
                if (metaValue !== undefined && metaValue !== null) {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  setValue(`${METADATA_KEY}.${metaField}`, metaValue as any, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }
              });
            } else {
              const valueToSet =
                entry.value !== undefined ? entry.value : value;
              if (valueToSet !== undefined && valueToSet !== null) {
                setValue(field, valueToSet, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              }
            }
          });
        }
      }
    }
  }, [clearErrors, dto, entity, isDirty, isDuplicate, isEdit, reset, setValue]);

  useConditionalFormRevalidate({
    trigger,
    isEdit,
    dto,
    yupSchemaDynamic,
    getValues,
    clearErrors,
  });

  return { methods, control };
};
